import { useMemo } from 'react';

import type { FieldSuggestionOption } from '../InputGroupWithSuggestions';
import { useAcceptedSuggestionIndex } from './useAcceptedSuggestionIndex';

type UseSuggestionDerivedStateProps = {
  localValue: string | null;
  suggestions: FieldSuggestionOption[];
};

export const useSuggestionDerivedState = ({
  localValue,
  suggestions,
}: UseSuggestionDerivedStateProps) => {
  const acceptedSuggestionIndex = useAcceptedSuggestionIndex(
    localValue,
    suggestions,
  );

  const defaultOptionContent = useMemo(
    () =>
      !localValue
        ? { label: 'Leave this blank', value: '' }
        : acceptedSuggestionIndex.caseSensitive >= 0
        ? { label: 'Clear this field', value: '' }
        : { label: `Use "${localValue}"`, value: localValue.trim() },
    [localValue, acceptedSuggestionIndex.caseSensitive],
  );

  const calculatedRows = useMemo(
    () => (!localValue ? 1 : localValue.split('\n').length),
    [localValue],
  );

  const hasSuggestions = suggestions.length > 0;
  const acceptedSuggestion = acceptedSuggestionIndex.caseSensitive >= 0;
  const availableSuggestions = localValue === null;

  return {
    acceptedSuggestionIndex,
    defaultOptionContent,
    calculatedRows,
    hasSuggestions,
    acceptedSuggestion,
    availableSuggestions,
  };
};
